import { Component, Inject, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BREEDS } from '../../../../assets/data/breeds';
import {
  EGG_MARKETING_STANDARDS,
  HOUSING_SYSTEMS,
} from '../../../../assets/data/eggMarketingStandards';
import { Barn } from '../../../settings/models/barn.model';
import { getBarns } from '../../../settings/store/selectors/barn.selector';
import Utils from '../../../shared/utils';
import {
  BeddingType,
  Breed,
  DrinkSystem,
  DrinkSystemName,
  EggMarketingStandard,
  FLOCK_VARIANT,
  FeedSystem,
  FeedSystemName,
  Flock,
  FlockVariant,
  HenArrival,
  HenHatchery,
  HousingSystem,
  Nest,
  NestName,
  RearingOrganization,
} from '../../models/flock.model';
import { upsertFlockRequest } from '../../store/actions/flock.actions';
import { getHatcheries, getRearingOrganizations } from '../../store/selectors/flock.selectors';
import { ArrivalDetailVariantConfig } from '../shared/flock-variant.config';

export interface ArrivalDialogInputData extends AbstractFormDialogData {
  flock: Flock;
  arrivalConfig: ArrivalDetailVariantConfig;
}

export interface ArrivalDialogResultData {
  arrival: HenArrival;
}

@Component({
  selector: 'app-arrival-detail',
  templateUrl: './arrival-detail.component.html',
  styleUrls: ['./arrival-detail.component.scss'],
  standalone: false,
})
export class ArrivalDetailComponent
  extends AbstractFormDialog<ArrivalDialogResultData>
  implements OnDestroy
{
  readonly barns = toSignal(this.store.select(getBarns), { initialValue: [] as Barn[] });
  readonly hatcheries = toSignal(this.store.select(getHatcheries), {
    initialValue: [] as HenHatchery[],
  });
  readonly rearingOrganizations = toSignal(this.store.select(getRearingOrganizations), {
    initialValue: [] as RearingOrganization[],
  });
  subscription = new Subscription();
  unsubscribeSignal: Subject<void> = new Subject<void>();

  breeds: Breed[] = BREEDS.filter((breed) => breed.id > 0);
  eggMarketingStandards: EggMarketingStandard[] = EGG_MARKETING_STANDARDS;
  housingSystems: HousingSystem[] = HOUSING_SYSTEMS;
  beddingTypes = Object.values(BeddingType);
  feedSystems: FeedSystemName[] = ['Ketting', 'Pannen', 'Spiraal'];
  drinkSystems: DrinkSystemName[] = ['Cups', 'Nippels', 'RondDrinkers'];
  nests: NestName[] = ['WegrolNest', 'Handgeraapt'];
  viewMode = 'general-flock-data';
  minDeviation: Date;
  maxDeviation: Date;
  minArrivalDate: Date = new Date(0);
  maxArrivalDate: Date = new Date(0);
  minDeviationInDays = 15;
  maxDeviationInDays = 6;
  currentDeviation?: number;
  hasWeekRegistrations = this.data.flock?.weekRegistrations?.length > 0;
  arrivalConfig = this.getArrivalConfig();
  flockVariant: FlockVariant = this.arrivalConfig.flockVariant;
  advancedSettingsArray = [
    'mortality',
    'eggsAmount',
    ...(this.flockVariant === FLOCK_VARIANT.Layer ? ['eggsAmountSecondKind'] : []),
    'eggWeight',
    'forage',
    'water',
    'henWeight',
    'isInMoult',
  ];
  hasNoBeddingType = this.data.flock?.arrival?.beddingType === BeddingType.NoBedding;
  formGroup = new FormGroup({
    flockName: new FormControl<string>('', [Validators.required]),
    barnId: new FormControl<number>(null, [Validators.required]),
    chickBirthDate: new FormControl<string>({ value: '', disabled: this.hasWeekRegistrations }, [
      Validators.required,
    ]),
    arrivalDate: new FormControl<string>({ value: '', disabled: this.hasWeekRegistrations }, [
      Validators.required,
    ]),
    productionPeriodStartDate: new FormControl<string>(
      { value: '', disabled: this.hasWeekRegistrations },
      [Validators.required],
    ),
    productionPeriodAge: new FormControl<number>(
      { value: null, disabled: this.hasWeekRegistrations },
      [],
    ),
    breedId: new FormControl<number>(null, [Validators.required, Validators.min(1)]),
    amountOfHens: new FormControl<number>(0, [Validators.required, Validators.min(0)]),
    showRoosterFields: new FormControl<boolean>(false),
    amountOfRoosters: new FormControl<number>(null, [Validators.min(0)]),
    henWeightInGrams: new FormControl<number>(null, [
      Validators.min(0),
      this.arrivalWeightValidator(),
    ]),
    roosterWeightInGrams: new FormControl<number>(null, [Validators.min(0)]),
    eggMarketingStandard: new FormControl<number>(null, [Validators.required]),
    housingSystem: new FormControl<number>(null, [Validators.required]),
    hatcheryId: new FormControl<number>(null, [Validators.required]),
    feedSystemHens: new FormControl<FeedSystem>(null),
    feedSystemRoosters: new FormControl<FeedSystem>(null),
    drinkSystem: new FormControl<DrinkSystem>(null),
    nest: new FormControl<Nest>(null),
    rearingOrganizationId: new FormControl<number>(null),
    beddingType: new FormControl<BeddingType>(null),
    beddingAmount: new FormControl<number>({ value: null, disabled: this.hasNoBeddingType }, [
      Validators.min(0),
    ]),
    transportDistance: new FormControl<number>(75, [Validators.min(0), Validators.max(1000)]),
    timeInFreeRange: new FormControl<number>(null, [Validators.min(0), Validators.max(100)]),
    inputMode: new FormControl<string>({ value: 'PerDay', disabled: this.hasWeekRegistrations }, [
      Validators.required,
    ]),
    advancedSettings: new FormArray([]),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ArrivalDialogInputData,
    protected mdDialogRef: MatDialogRef<
      ArrivalDetailComponent,
      AbstractDialogResultData<ArrivalDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private locale: LocaleService,
  ) {
    super();
    this.data.title = this.locale.translate('arrival-detail.title-create');
    this.configureVariantValidators();

    this.breeds = this.breeds.slice().sort((a, b) => a.name.localeCompare(b.name));

    this.advancedSettingsArray.forEach((_) => {
      this.advancedSettings().push(new FormControl<string>('PerDay'));
    });

    if (!!this.data.flock && !!this.data.flock.arrival) {
      this.data.title = `${this.locale.translate('arrival-detail.title-edit')} ${
        this.data.flock.name
      }`;
      if (
        this.data.flock.arrival.breed?.id > 0 &&
        !this.breeds.some((d) => d.id === this.data.flock.arrival.breed?.id)
      ) {
        this.breeds.push(this.data.flock.arrival.breed);
      }
      this.formGroup.controls.flockName.setValue(this.data.flock.name);
      this.formGroup.controls.barnId.patchValue(this.data.flock.arrival.barn?.id);
      this.formGroup.controls.chickBirthDate.setValue(this.data.flock.arrival.chickBirthDate);
      this.formGroup.controls.arrivalDate.setValue(this.data.flock.arrival.arrivalDate);
      this.currentDeviation = this.data.flock.arrival.deviationOfBirthDateInDays;
      if (this.data.flock.arrival.chickBirthDate) {
        this.minDeviation = new Date(this.data.flock.arrival.chickBirthDate);
        this.minDeviation.setDate(this.getMinDeviation());
        this.maxDeviation = new Date(this.data.flock.arrival.chickBirthDate);
        this.maxDeviation.setDate(this.getMaxDeviation());

        this.minArrivalDate = new Date(this.data.flock.arrival.chickBirthDate);
        this.minArrivalDate.setDate(this.minArrivalDate.getDate() + 1);

        this.formGroup.controls.productionPeriodAge.setValue(
          this.defaultProductionPeriodStartInDays +
            (this.data.flock.arrival.deviationOfBirthDateInDays ?? 0),
        );

        const productionPeriodStartDate = new Date(this.data.flock.arrival.chickBirthDate);
        productionPeriodStartDate.setDate(
          productionPeriodStartDate.getDate() +
            this.data.flock.arrival.deviationOfBirthDateInDays +
            this.defaultProductionPeriodStartInDays,
        );
        // arrivaldate must be before the prod.period startdate
        this.maxArrivalDate = new Date(productionPeriodStartDate);
        this.maxArrivalDate.setDate(this.maxArrivalDate.getDate());

        this.formGroup.controls.productionPeriodStartDate.setValue(
          productionPeriodStartDate.toISOString(),
        );
      }

      this.formGroup.controls.breedId.setValue(this.data.flock.arrival.breed?.id);
      this.formGroup.controls.amountOfHens.setValue(this.data.flock.arrival.amountOfHens);
      this.formGroup.controls.showRoosterFields.setValue(this.hasRoosterData());
      this.formGroup.controls.amountOfRoosters.setValue(this.data.flock.arrival.amountOfRoosters);
      this.formGroup.controls.henWeightInGrams.setValue(this.data.flock.arrival.henWeightInGrams);
      this.formGroup.controls.roosterWeightInGrams.setValue(
        this.data.flock.arrival.roosterWeightInGrams,
      );
      this.formGroup.controls.eggMarketingStandard.setValue(
        this.data.flock.arrival.eggMarketingStandard,
      );
      this.formGroup.controls.housingSystem.setValue(this.data.flock.arrival.housingSystem);
      this.formGroup.controls.hatcheryId.setValue(this.data.flock.arrival.hatchery?.id);
      this.formGroup.controls.feedSystemHens.setValue(
        this.getLookupName(this.data.flock.arrival.feedSystemHens),
      );
      this.formGroup.controls.feedSystemRoosters.setValue(
        this.getLookupName(this.data.flock.arrival.feedSystemRoosters),
      );
      this.formGroup.controls.drinkSystem.setValue(
        this.getLookupName(this.data.flock.arrival.drinkSystem),
      );
      this.formGroup.controls.nest.setValue(this.getLookupName(this.data.flock.arrival.nest));
      this.formGroup.controls.rearingOrganizationId.setValue(
        this.data.flock.arrival.rearingOrganization?.id,
      );
      this.formGroup.controls.beddingType.setValue(this.data.flock.arrival.beddingType);
      this.formGroup.controls.beddingAmount.setValue(this.data.flock.arrival.beddingAmount);
      this.formGroup.controls.transportDistance.setValue(
        this.data.flock.arrival.transportDistance ?? 75,
      );
      this.formGroup.controls.timeInFreeRange.setValue(this.data.flock.arrival.timeInFreeRange);

      const inputDefinition = this.data.flock.inputDefinition as unknown as Record<string, string>;
      const inputDefinitionArray = this.advancedSettingsArray.map(
        (setting) => inputDefinition[setting === 'isInMoult' ? 'moult' : setting],
      );
      const isAllSame =
        inputDefinitionArray.filter((v, _, a) => v === a[0]).length === inputDefinitionArray.length;
      if (isAllSame) {
        this.formGroup.controls.inputMode.setValue(this.data.flock.inputDefinition.mortality);
      } else {
        this.formGroup.controls.inputMode.setValue('Advanced');
        this.advancedSettings().controls.forEach((control: FormControl, i: number) =>
          control.setValue(inputDefinitionArray[i]),
        );
      }
    }

    // calculate min / max productionPeriodStartDate according to selected birthdate
    this.subscription.add(
      this.formGroup.controls.chickBirthDate.valueChanges
        .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
        .subscribe((birthdateString: string) => {
          const birthdate = new Date(birthdateString);
          if (birthdate) {
            const defaultStartProductionPeriod = new Date(birthdate);
            defaultStartProductionPeriod.setDate(
              defaultStartProductionPeriod.getDate() + this.defaultProductionPeriodStartInDays,
            );

            this.minDeviation = new Date(defaultStartProductionPeriod);
            this.minDeviation.setDate(this.minDeviation.getDate() - this.minDeviationInDays);
            this.maxDeviation = new Date(defaultStartProductionPeriod);
            this.maxDeviation.setDate(this.maxDeviation.getDate() + this.maxDeviationInDays);

            this.minArrivalDate = new Date(birthdate);
            this.minArrivalDate.setDate(this.minArrivalDate.getDate() + 1);

            const difference =
              Math.round(
                new Date(this.formGroup.controls.arrivalDate.value).getTime() - birthdate.getTime(),
              ) /
              (1000 * 3600 * 24);

            if (difference < 1) {
              this.formGroup.controls.arrivalDate.patchValue(null, {
                emitEvent: false,
                onlySelf: true,
              });
            }

            // Update the production period based on the offset/deviation it was.
            defaultStartProductionPeriod.setDate(
              defaultStartProductionPeriod.getDate() + (this.currentDeviation ?? 0),
            );

            this.formGroup.controls.productionPeriodStartDate.patchValue(
              defaultStartProductionPeriod.toISOString(),
            );
          }
        }),
    );

    this.subscription.add(
      this.formGroup.controls.arrivalDate.valueChanges
        .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
        .subscribe((arrdate: string) => {
          if (!this.formGroup.controls.chickBirthDate.value) {
            this.minArrivalDate = new Date(arrdate);
            this.minArrivalDate.setDate(this.minArrivalDate.getDate() + 1);

            this.formGroup.controls.chickBirthDate.patchValue(arrdate, {
              emitEvent: false,
              onlySelf: true,
            });
          }
        }),
    );

    this.subscription.add(
      this.formGroup.controls.productionPeriodStartDate.valueChanges
        .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
        .subscribe((dateString: string) => {
          const date = new Date(dateString);
          if (date) {
            const ageInDays = this.calculateAgeInDaysAtStartOfProductionPeriod();
            this.formGroup.controls.productionPeriodAge.patchValue(ageInDays);
            this.currentDeviation = this.getDeviationInDays();

            // arrivaldate must be before the prod.period startdate
            this.maxArrivalDate = new Date(date);
            this.maxArrivalDate.setDate(this.maxArrivalDate.getDate());

            const difference =
              Math.round(
                date.getTime() -
                  new Date(<string>this.formGroup.controls.arrivalDate.value).getTime(),
              ) /
              (1000 * 3600 * 24);

            if (difference < 1) {
              this.formGroup.controls.arrivalDate.patchValue(null, {
                emitEvent: false,
                onlySelf: true,
              });
            }
          }
        }),
    );

    this.subscription.add(
      this.formGroup.controls.housingSystem.valueChanges
        .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
        .subscribe(() => {
          if (this.isOrganicOrFreeRange()) {
            this.formGroup.controls.timeInFreeRange.setValue(15);
          } else {
            this.formGroup.controls.timeInFreeRange.setValue(0);
          }
        }),
    );

    this.subscription.add(
      this.formGroup.controls.showRoosterFields.valueChanges
        .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
        .subscribe((showRoosterFields: boolean) => {
          if (!showRoosterFields) {
            this.clearRoosterFields();
          }
        }),
    );

    this.subscription.add(
      this.formGroup.controls.beddingType.valueChanges
        .pipe(takeUntil(this.unsubscribeSignal))
        .subscribe((beddingType: BeddingType) => {
          if (beddingType === BeddingType.NoBedding) {
            this.formGroup.controls.beddingAmount.disable();
            this.formGroup.controls.beddingAmount.setValue(null);
          } else {
            this.formGroup.controls.beddingAmount.enable();
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.unsubscribeSignal.next();
    this.unsubscribeSignal.unsubscribe();
    this.subscription.unsubscribe();
  }

  calculateAgeInDaysAtStartOfProductionPeriod(): number {
    const daysDifference =
      Math.abs(
        new Date(<string>this.formGroup.controls.chickBirthDate.value).getTime() -
          new Date(<string>this.formGroup.controls.productionPeriodStartDate.value).getTime(),
      ) /
      (1000 * 3600 * 24);
    return Math.round(daysDifference);
  }

  get defaultProductionPeriodStartInDays(): number {
    return this.arrivalConfig.productionPeriod.startInDays;
  }

  private getArrivalConfig(): ArrivalDetailVariantConfig {
    return this.data.arrivalConfig;
  }

  advancedSettings(): FormArray {
    return this.formGroup.get('advancedSettings') as FormArray;
  }

  getMinDeviation(): number {
    return (
      this.minDeviation.getDate() +
      this.defaultProductionPeriodStartInDays -
      this.minDeviationInDays
    );
  }

  getMaxDeviation(): number {
    return (
      this.maxDeviation.getDate() +
      this.defaultProductionPeriodStartInDays +
      this.maxDeviationInDays
    );
  }

  getDeviationInDays(): number {
    const productionPeriodDate: Date = new Date(
      <string>this.formGroup.controls.productionPeriodStartDate.value,
    );
    const birthDate: Date = new Date(<string>this.formGroup.controls.chickBirthDate.value);
    const diffTime = +productionPeriodDate - +birthDate;
    const diffDays =
      Math.round(diffTime / (1000 * 3600 * 24)) - this.defaultProductionPeriodStartInDays;
    return diffDays;
  }

  save(): void {
    const setInputDefinition = () => {
      if (<string>this.formGroup.controls.inputMode.value !== 'Advanced') {
        return {
          mortality: <string>this.formGroup.controls.inputMode.value,
          eggsAmount: <string>this.formGroup.controls.inputMode.value,
          eggsAmountSecondKind: <string>this.formGroup.controls.inputMode.value,
          eggWeight: <string>this.formGroup.controls.inputMode.value,
          forage: <string>this.formGroup.controls.inputMode.value,
          water: <string>this.formGroup.controls.inputMode.value,
          henWeight: <string>this.formGroup.controls.inputMode.value,
          moult: <string>this.formGroup.controls.inputMode.value,
        };
      } else {
        const advancedValue = (setting: string) =>
          (<string[]>this.advancedSettings().value)[this.advancedSettingsArray.indexOf(setting)];
        const eggsAmount = advancedValue('eggsAmount');
        return {
          mortality: advancedValue('mortality'),
          eggsAmount,
          eggsAmountSecondKind:
            this.flockVariant === FLOCK_VARIANT.Breeder
              ? eggsAmount
              : advancedValue('eggsAmountSecondKind'),
          eggWeight: advancedValue('eggWeight'),
          forage: advancedValue('forage'),
          water: advancedValue('water'),
          henWeight: advancedValue('henWeight'),
          moult: advancedValue('isInMoult'),
        };
      }
    };

    if (this.formGroup.valid) {
      const barns = this.barns();
      const hatcheries = this.hatcheries();
      const rearingOrganizations = this.rearingOrganizations();

      const arrivalBase = {
        arrivalDate:
          typeof (<unknown>this.formGroup.controls.arrivalDate.value) !== 'string'
            ? Utils.toUtcDate(new Date(this.formGroup.controls.arrivalDate.value)).toISOString()
            : <string>this.formGroup.controls.arrivalDate.value,
        breed: <Breed>this.breeds.find((b) => b.id === this.formGroup.controls.breedId.value),
        barn: <Barn>barns.find((b) => b.id === this.formGroup.controls.barnId.value),
        hatchery: <HenHatchery>(
          hatcheries.find((h) => h.id === this.formGroup.controls.hatcheryId.value)
        ),
        chickBirthDate:
          typeof (<unknown>this.formGroup.controls.chickBirthDate.value) !== 'string'
            ? Utils.toUtcDate(new Date(this.formGroup.controls.chickBirthDate.value)).toISOString()
            : <string>this.formGroup.controls.chickBirthDate.value,
        amountOfHens: <number>this.formGroup.controls.amountOfHens.value,
        henWeightInGrams: this.formGroup.controls.henWeightInGrams.value,
        deviationOfBirthDateInDays: this.getDeviationInDays(),
        transportDistance: this.formGroup.controls.transportDistance.value,
      };

      const arrival: HenArrival = this.isBreeder
        ? {
            ...arrivalBase,
            feedSystemHens: this.formGroup.controls.feedSystemHens.value,
            drinkSystem: this.formGroup.controls.drinkSystem.value,
            nest: this.formGroup.controls.nest.value,
            rearingOrganization:
              rearingOrganizations.find(
                (item) => item.id === this.formGroup.controls.rearingOrganizationId.value,
              ) ?? undefined,
            ...(this.formGroup.controls.showRoosterFields.value
              ? {
                  amountOfRoosters: this.formGroup.controls.amountOfRoosters.value,
                  roosterWeightInGrams: this.formGroup.controls.roosterWeightInGrams.value,
                  feedSystemRoosters: this.formGroup.controls.feedSystemRoosters.value,
                }
              : {}),
          }
        : {
            ...arrivalBase,
            eggMarketingStandard: this.formGroup.controls.eggMarketingStandard.value,
            housingSystem: this.formGroup.controls.housingSystem.value,
            beddingType: this.formGroup.controls.beddingType.value,
            beddingAmount: this.formGroup.controls.beddingAmount.value,
            timeInFreeRange: this.formGroup.controls.timeInFreeRange.value,
          };

      const flock = {
        ...this.data.flock,
        name: this.formGroup.controls.flockName.value,
        arrival,
        inputDefinition: setInputDefinition(),
      };

      this.store.dispatch(upsertFlockRequest({ request: flock }));
      this.confirm({
        arrival: flock.arrival,
      });
    }
  }

  isOrganicOrFreeRange(): boolean {
    const selectedHousingSystem = this.formGroup.get('housingSystem')?.value;
    const housingSystemName = this.housingSystems.find(
      (system) => system.id === selectedHousingSystem,
    )?.name;

    return housingSystemName === 'organic' || housingSystemName === 'free-range';
  }

  arrivalWeightValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const henWeightOnDeparture = this.data.flock.departure?.weightInGrams;
      const henWeightOnArrival = control.value;

      if (
        !henWeightOnDeparture ||
        !henWeightOnArrival ||
        henWeightOnDeparture > henWeightOnArrival
      ) {
        return null;
      }

      return { arrivalWeightInvalid: true };
    };
  }

  get isBreeder(): boolean {
    return this.flockVariant === FLOCK_VARIANT.Breeder;
  }

  private configureVariantValidators(): void {
    if (this.isBreeder) {
      this.formGroup.controls.amountOfRoosters.setValidators([Validators.min(0)]);
      this.formGroup.controls.feedSystemHens.setValidators([Validators.required]);
      this.formGroup.controls.drinkSystem.setValidators([Validators.required]);
      this.formGroup.controls.eggMarketingStandard.clearValidators();
      this.formGroup.controls.housingSystem.clearValidators();
      this.formGroup.controls.beddingAmount.clearValidators();
      this.formGroup.controls.timeInFreeRange.clearValidators();
    } else {
      this.formGroup.controls.amountOfRoosters.clearValidators();
      this.formGroup.controls.feedSystemHens.clearValidators();
      this.formGroup.controls.drinkSystem.clearValidators();
    }

    this.formGroup.controls.amountOfRoosters.updateValueAndValidity({ emitEvent: false });
    this.formGroup.controls.feedSystemHens.updateValueAndValidity({ emitEvent: false });
    this.formGroup.controls.drinkSystem.updateValueAndValidity({ emitEvent: false });
    this.formGroup.controls.eggMarketingStandard.updateValueAndValidity({ emitEvent: false });
    this.formGroup.controls.housingSystem.updateValueAndValidity({ emitEvent: false });
  }

  private hasRoosterData(): boolean {
    const arrival = this.data.flock?.arrival;

    return (
      arrival?.amountOfRoosters != null ||
      arrival?.roosterWeightInGrams != null ||
      arrival?.feedSystemRoosters != null
    );
  }

  private clearRoosterFields(): void {
    this.formGroup.controls.amountOfRoosters.setValue(null, { emitEvent: false });
    this.formGroup.controls.roosterWeightInGrams.setValue(null, { emitEvent: false });
    this.formGroup.controls.feedSystemRoosters.setValue(null, { emitEvent: false });
  }

  private getLookupName<TName extends string>(
    value?: TName | { name: TName } | null,
  ): TName | null {
    if (!value) {
      return null;
    }

    return typeof value === 'string' ? value : value.name;
  }
}
